/**
 * Interactive shop map — Leaflet/OSM view over filtered PublicCustomerSalesPointDTO rows.
 * No listability / fetch logic; parent owns drain + filters + geolocation acquire.
 *
 * HOST-OWNED framing (ADR-MV-02/04, UNIT-G3-*): framingMode shops|myLocation|radius|bounds;
 * FitBoundsWhenReady shops∪user only when shops; Circle + REQUIRED Rectangle;
 * map never calls getCurrentPosition. Views menu + wizard via createPortal(document.body).
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Circle,
  MapContainer,
  Marker,
  Rectangle,
  TileLayer,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import type { LatLngExpression } from 'leaflet';
import {
  beginSignInToSaveMapViews,
  buildGuestSignInMapViewDraft,
} from '../account/mapViewSignInGate.js';
import type { MapCustomViewDTO } from '../account/mapViewsTypes.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { Button } from '../../shared/ui/Button.js';
import { haversineMeters } from '../../shared/ui/directoryListFilter.js';
import { isGloballySignedInChrome } from '../hub/customerHubPaths.js';
import { ShopMapViewsMenu } from './components/ShopMapViewsMenu.js';
import {
  ShopMapViewWizard,
  type ShopMapViewWizardPrefill,
  type ShopMapWizardFramingPreview,
} from './components/ShopMapViewWizard.js';
import { shopMarkerIcon, userPositionIcon } from './leafletMapSetup.js';
import type { PublicCustomerSalesPointDTO } from './publicSalesPointsApi.js';
import { ShopMapMarkerCard } from './ShopMapMarkerCard.js';
import {
  approxCircleBoundsCorners,
  buildEphemeralAroundMeActive,
  buildGeneralActive,
  buildMyLocationActive,
  buildSavedViewActive,
  framingApplyFromSavedViewRow,
  normalizeMapViewBounds,
  radiusKmToMeters,
  readMapViewWizardDraftFromSession,
  resolveInitialMapViewActive,
  restoreHostFramingFromMapViewActive,
  shopMapSavedViewRowFromDto,
  writeMapViewActiveToSession,
  type ShopMapBuiltinId,
  type ShopMapBoundsOverlay,
  type ShopMapFramingMode,
  type ShopMapSavedViewRow,
} from './shopMapViewModel.js';
import {
  CZECHIA_FALLBACK_CENTER,
  DEFAULT_MAP_ZOOM,
  buildMapItems,
  computeFitBoundsKey,
  salesPointMapMarkerKey,
  latLngBoundsFromItems,
} from './shopMapUtils.js';
import {
  CUSTOMER_PRESENCE_MAP_OMITS_SHIPPING_ONLY_I18N_KEY,
} from './customerPresence.js';
import { shouldShowShippingOnlyGeoOmitEmpty } from './shippingOnlyGeoOmit.js';

/**
 * Leaflet `fitBounds` padding as `[x, y]`. Fixed 56px is fine on tall portrait
 * panes; on short landscape (~110–180px) 56×2 eats the whole height and stacks
 * markers on one pixel (G-FRAME-2 landscape).
 */
function fitBoundsPaddingXY(map: {
  getSize?: () => { x: number; y: number };
}): [number, number] {
  const size = typeof map.getSize === 'function' ? map.getSize() : null;
  const width = size && Number.isFinite(size.x) ? size.x : 0;
  const height = size && Number.isFinite(size.y) ? size.y : 0;
  if (height > 0 && height < 220) {
    const padX = Math.max(8, Math.min(40, Math.floor(width * 0.06)));
    const padY = Math.max(8, Math.min(20, Math.floor(height * 0.1)));
    return [padX, padY];
  }
  return [56, 56];
}

export type ShopsDirectoryMapRequestMyLocationOptions = {
  /** Around-me Apply — deny uses directory.mapView.gpsDenied* and clears pending km. */
  readonly purpose?: 'aroundMeApply';
  /** Called on geolocation deny/error so map clears awaitingAroundMeAfterAcquireRef. */
  readonly onDenied?: () => void;
};

export interface ShopsDirectoryMapUserPosition {
  readonly lat: number;
  readonly lng: number;
}

export interface ShopsDirectoryMapViewportState {
  readonly centerLat: number;
  readonly centerLng: number;
  readonly zoom: number;
}

/** Bail-out before setState when map camera unchanged (G6 — #185 viewport churn). */
export function shopsDirectoryMapViewportEqual(
  a: ShopsDirectoryMapViewportState,
  b: ShopsDirectoryMapViewportState,
): boolean {
  return (
    a.centerLat === b.centerLat &&
    a.centerLng === b.centerLng &&
    a.zoom === b.zoom
  );
}

export interface ShopsDirectoryMapProps {
  readonly visibleItems: readonly PublicCustomerSalesPointDTO[];
  readonly userPosition: ShopsDirectoryMapUserPosition | null;
  readonly selectedSalesPointId: number | null;
  /** Parent drains infinite pages; false while incomplete. */
  readonly mapReady: boolean;
  readonly onSelectSalesPointId: (id: number | null) => void;
  /**
   * Explicit “My location” / Around-me only — never called on mount.
   * Around-me Apply may pass `purpose: 'aroundMeApply'` + `onDenied` so GPS deny
   * clears pending km and uses directory.mapView.gpsDenied* copy (G18).
   */
  readonly onRequestMyLocation: (
    options?: ShopsDirectoryMapRequestMyLocationOptions,
  ) => void;
  /** Session persist tenant for map-card favorites (same as list rows). */
  readonly persistTenant: string;
  readonly hideTenantName?: boolean;
  /**
   * Map selection card tenant logo chip — Oblíbené hosts pass false (list parity).
   * Omit on platform Obchody to respect `showTenantLogoOnStorePhoto`.
   */
  readonly showTenantChipOnSelectionCard?: boolean;
  readonly onBackToList?: () => void;
  /**
   * Saved custom views with FREEZE geometry (parent maps MapCustomViewDTO → row).
   * Session remount holds viewId+mode only — geometry comes from this list.
   */
  readonly savedViews?: readonly ShopMapSavedViewRow[];
  /**
   * Parent query still unsettled (G2/G3 — prefer RQ `isPending`, incl. offline/paused).
   * Empty `savedViews` while true must not false-General; settled empty/missing
   * apply → handleSelectGeneral. Settled **error** must not false-General — use
   * `savedViewsError` + retry instead.
   */
  readonly savedViewsLoading?: boolean;
  /** List/restore query failed — show retry; do not treat as empty views. */
  readonly savedViewsError?: boolean;
  readonly onRetrySavedViews?: () => void;
  /** Optional side-effect after host applies framing (e.g. analytics). */
  readonly onApplySavedView?: (viewId: number) => void;
  readonly onRequestDeleteView?: (viewId: number) => void;
  readonly onRequestRenameView?: (
    viewId: number,
    name: string,
  ) => Promise<void>;
  readonly deletePending?: boolean;
  readonly renamePending?: boolean;
  /** Optional host-owned restore/persist bridge for map camera session state. */
  readonly restoredViewport?: ShopsDirectoryMapViewportState | null;
  readonly onViewportChange?: (next: ShopsDirectoryMapViewportState) => void;
  /**
   * When false, host owns selection UI (e.g. discovery multi-offer picker) and
   * ShopMapMarkerCard is not shown. Default true for shops directory.
   */
  readonly showSelectionCard?: boolean;
  /**
   * G9/G4 — discovery/browse map owns camera via restoredViewport + discovery session.
   * When true: skip shops `MAP_VIEW_ACTIVE` resolve/write, hide Views chrome, and
   * lock My location / Around me / radius–bounds camera mutations.
   */
  readonly disableShopsSessionFraming?: boolean;
  /**
   * G4 — hide Views trigger / menu / wizard without implying shops session lock.
   * Also implied by `disableShopsSessionFraming` (browse passes both).
   */
  readonly hideViewsChrome?: boolean;
}

function MapClickClear({ onClear }: { readonly onClear: () => void }): null {
  useMapEvents({
    click() {
      onClear();
    },
  });
  return null;
}

/**
 * Fit shop bounds (+ user when known) when framingMode === 'shops' only
 * (UNIT-G3-FIT-NOOP-R|B|M).
 */
function FitBoundsWhenReady({
  boundsKey,
  corners,
  userPosition,
  mapReady,
  userPresenceKey,
  fitEpoch,
  framingMode,
  suppressFitForRestoredViewport,
}: {
  readonly boundsKey: string;
  readonly corners: ReturnType<typeof latLngBoundsFromItems>;
  readonly userPosition: ShopsDirectoryMapUserPosition | null;
  readonly mapReady: boolean;
  readonly userPresenceKey: '0' | '1';
  readonly fitEpoch: number;
  readonly framingMode: ShopMapFramingMode;
  /** G16/G9 — host restored camera must not be wiped by shops∪user FitBounds. */
  readonly suppressFitForRestoredViewport: boolean;
}): null {
  const map = useMap();
  const cornersRef = useRef(corners);
  const userPositionRef = useRef(userPosition);

  useEffect(() => {
    cornersRef.current = corners;
    userPositionRef.current = userPosition;
  }, [corners, userPosition]);

  // Hermetic E2E camera probes attach via container._leaflet_map (G-FRAME-GATE).
  useEffect(() => {
    const container = map.getContainer?.();
    if (container) {
      (container as HTMLElement & { _leaflet_map?: typeof map })._leaflet_map = map;
    }
  }, [map]);

  useEffect(() => {
    if (!mapReady || framingMode !== 'shops' || suppressFitForRestoredViewport) {
      return;
    }

    const applyFit = (): void => {
      const currentCorners = cornersRef.current;
      const pos = userPositionRef.current;
      if (currentCorners) {
        let south = currentCorners[0][0];
        let west = currentCorners[0][1];
        let north = currentCorners[1][0];
        let east = currentCorners[1][1];
        if (pos !== null) {
          south = Math.min(south, pos.lat);
          north = Math.max(north, pos.lat);
          west = Math.min(west, pos.lng);
          east = Math.max(east, pos.lng);
        }
        map.fitBounds(
          [
            [south, west],
            [north, east],
          ],
          { padding: fitBoundsPaddingXY(map), maxZoom: 14 },
        );
        return;
      }
      if (pos) {
        map.setView([pos.lat, pos.lng], DEFAULT_MAP_ZOOM);
        return;
      }
      map.setView(
        [CZECHIA_FALLBACK_CENTER[0], CZECHIA_FALLBACK_CENTER[1]],
        DEFAULT_MAP_ZOOM,
      );
    };

    if (typeof map.invalidateSize === 'function') {
      map.invalidateSize({ animate: false });
    }
    applyFit();

    const frame = window.requestAnimationFrame(() => {
      if (typeof map.invalidateSize === 'function') {
        map.invalidateSize({ animate: false });
      }
      applyFit();
    });

    const container = map.getContainer?.();
    let resizeObserver: ResizeObserver | null = null;
    if (
      container instanceof HTMLElement &&
      typeof ResizeObserver === 'function'
    ) {
      let lastH = container.getBoundingClientRect().height;
      resizeObserver = new ResizeObserver(() => {
        const nextH = container.getBoundingClientRect().height;
        if (Math.abs(nextH - lastH) < 8) {
          return;
        }
        lastH = nextH;
        if (typeof map.invalidateSize === 'function') {
          map.invalidateSize({ animate: false });
        }
        applyFit();
      });
      resizeObserver.observe(container);
    }

    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
    };
  }, [
    boundsKey,
    fitEpoch,
    framingMode,
    map,
    mapReady,
    suppressFitForRestoredViewport,
    userPresenceKey,
  ]);
  return null;
}

/** G-FRAME-6 — pan-only when framingMode === 'myLocation' and panToken > 0. */
function PanToUserPosition({
  userPosition,
  panToken,
  framingMode,
  lockHostCamera,
}: {
  readonly userPosition: ShopsDirectoryMapUserPosition | null;
  readonly panToken: number;
  readonly framingMode: ShopMapFramingMode;
  /** G4 — discovery restored / browse isolation must not be wiped by My location. */
  readonly lockHostCamera: boolean;
}): null {
  const map = useMap();
  useEffect(() => {
    if (
      lockHostCamera ||
      framingMode !== 'myLocation' ||
      panToken === 0 ||
      userPosition === null
    ) {
      return;
    }
    map.setView(
      [userPosition.lat, userPosition.lng],
      Math.max(map.getZoom(), 14),
    );
  }, [framingMode, lockHostCamera, map, panToken, userPosition]);
  return null;
}

/** Fit Circle after Around-me Apply / saved radius views (G-FRAME-R). */
function FitRadiusOverlay({
  center,
  radiusKm,
  radiusFitEpoch,
  framingMode,
  lockHostCamera,
}: {
  readonly center: ShopsDirectoryMapUserPosition | null;
  readonly radiusKm: number | null;
  readonly radiusFitEpoch: number;
  readonly framingMode: ShopMapFramingMode;
  /** G4 — discovery restored / browse isolation must not be wiped by radius fit. */
  readonly lockHostCamera: boolean;
}): null {
  const map = useMap();
  useEffect(() => {
    if (
      lockHostCamera ||
      framingMode !== 'radius' ||
      center === null ||
      radiusKm === null ||
      radiusFitEpoch === 0
    ) {
      return;
    }
    const corners = approxCircleBoundsCorners(
      center.lat,
      center.lng,
      radiusKm,
    );
    map.fitBounds(
      [
        [corners[0][0], corners[0][1]],
        [corners[1][0], corners[1][1]],
      ],
      { padding: fitBoundsPaddingXY(map), maxZoom: 16 },
    );
  }, [center, framingMode, lockHostCamera, map, radiusFitEpoch, radiusKm]);
  return null;
}

/** Fit saved bounds Rectangle (G-FRAME-GATE-B). */
function FitBoundsOverlay({
  overlay,
  boundsFitEpoch,
  framingMode,
  lockHostCamera,
}: {
  readonly overlay: ShopMapBoundsOverlay | null;
  readonly boundsFitEpoch: number;
  readonly framingMode: ShopMapFramingMode;
  /** G4 — discovery restored / browse isolation must not be wiped by bounds fit. */
  readonly lockHostCamera: boolean;
}): null {
  const map = useMap();
  useEffect(() => {
    if (
      lockHostCamera ||
      framingMode !== 'bounds' ||
      overlay === null ||
      boundsFitEpoch === 0
    ) {
      return;
    }
    map.fitBounds(
      [
        [overlay.swLat, overlay.swLng],
        [overlay.neLat, overlay.neLng],
      ],
      { padding: fitBoundsPaddingXY(map), maxZoom: 16 },
    );
  }, [boundsFitEpoch, framingMode, lockHostCamera, map, overlay]);
  return null;
}

/** Expose Leaflet getBounds to the create wizard (map_camera @ 6 dp). */
function MapCameraBoundsBridge({
  boundsGetterRef,
}: {
  readonly boundsGetterRef: MutableRefObject<
    (() => ShopMapBoundsOverlay | null) | null
  >;
}): null {
  const map = useMap();
  useEffect(() => {
    boundsGetterRef.current = (): ShopMapBoundsOverlay | null => {
      const b = map.getBounds();
      const sw = b.getSouthWest();
      const ne = b.getNorthEast();
      return normalizeMapViewBounds({
        swLat: sw.lat,
        swLng: sw.lng,
        neLat: ne.lat,
        neLng: ne.lng,
      });
    };
    return (): void => {
      boundsGetterRef.current = null;
    };
  }, [boundsGetterRef, map]);
  return null;
}

function MapViewportSessionBridge({
  restoredViewport,
  onViewportChange,
}: {
  readonly restoredViewport: ShopsDirectoryMapViewportState | null;
  readonly onViewportChange?: (next: ShopsDirectoryMapViewportState) => void;
}): null {
  const map = useMap();
  const restoreAppliedRef = useRef(false);
  const onViewportChangeRef = useRef(onViewportChange);

  // G6 — stable publishViewport; parent may pass fresh inline callbacks each render.
  useLayoutEffect(() => {
    onViewportChangeRef.current = onViewportChange;
  });

  const publishViewport = useCallback((): void => {
    const handler = onViewportChangeRef.current;
    if (handler === undefined) {
      return;
    }
    const center = map.getCenter?.();
    const zoomRaw = map.getZoom?.();
    if (
      center === undefined ||
      typeof center.lat !== 'number' ||
      !Number.isFinite(center.lat) ||
      typeof center.lng !== 'number' ||
      !Number.isFinite(center.lng) ||
      typeof zoomRaw !== 'number' ||
      !Number.isFinite(zoomRaw)
    ) {
      return;
    }
    handler({
      centerLat: center.lat,
      centerLng: center.lng,
      zoom: Math.round(zoomRaw),
    });
  }, [map]);

  useEffect(() => {
    if (restoreAppliedRef.current || restoredViewport === null) {
      return;
    }
    restoreAppliedRef.current = true;
    map.setView(
      [restoredViewport.centerLat, restoredViewport.centerLng],
      restoredViewport.zoom,
    );
  }, [map, restoredViewport]);

  useMapEvents({
    moveend: publishViewport,
    zoomend: publishViewport,
  });

  useEffect(() => {
    return () => {
      publishViewport();
    };
  }, [publishViewport]);

  return null;
}

export function ShopsDirectoryMap({
  visibleItems,
  userPosition,
  selectedSalesPointId,
  mapReady,
  onSelectSalesPointId,
  onRequestMyLocation,
  persistTenant,
  hideTenantName = false,
  showTenantChipOnSelectionCard,
  onBackToList,
  savedViews = [],
  savedViewsLoading = false,
  savedViewsError = false,
  onRetrySavedViews,
  onApplySavedView,
  onRequestDeleteView,
  onRequestRenameView,
  deletePending = false,
  renamePending = false,
  restoredViewport = null,
  onViewportChange,
  showSelectionCard = true,
  disableShopsSessionFraming = false,
  hideViewsChrome: hideViewsChromeProp = false,
}: ShopsDirectoryMapProps): JSX.Element {
  const { t } = useTranslation('customer');
  const navigate = useNavigate();
  const location = useLocation();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const signedIn = isGloballySignedInChrome({ session });

  const [tileError, setTileError] = useState(false);
  const [tileEpoch, setTileEpoch] = useState(0);
  /** G9 — once host restored camera applied, keep FitBounds suppressed across boundsKey/fitEpoch churn. */
  const [suppressFitForRestoredViewport, setSuppressFitForRestoredViewport] = useState(
    () => restoredViewport !== null,
  );
  if (restoredViewport !== null && !suppressFitForRestoredViewport) {
    setSuppressFitForRestoredViewport(true);
  }
  /**
   * G4 — host-owned discovery camera: suppress shops∪user FitBounds (G9 latch) and
   * also My location / radius / bounds overlays that still setView/fitBounds.
   */
  const lockHostCamera =
    disableShopsSessionFraming || suppressFitForRestoredViewport;
  /** G4 — browse/discovery: hide Views chrome entirely (handlers no-op when locked). */
  const hideViewsChrome = disableShopsSessionFraming || hideViewsChromeProp;
  const initialSessionFraming = useMemo(() => {
    // G9 — discovery/host restored camera or isolated browse map: never adopt shops MAP_VIEW_ACTIVE.
    if (disableShopsSessionFraming || restoredViewport !== null) {
      return null;
    }
    const { active, restoredFromSession } = resolveInitialMapViewActive();
    if (!restoredFromSession) {
      return null;
    }
    return restoreHostFramingFromMapViewActive(active);
  }, [disableShopsSessionFraming, restoredViewport]);
  const persistShopsMapViewActive = useCallback(
    (value: Parameters<typeof writeMapViewActiveToSession>[0]): void => {
      if (disableShopsSessionFraming) {
        return;
      }
      writeMapViewActiveToSession(value);
    },
    [disableShopsSessionFraming],
  );
  const [panToken, setPanToken] = useState(
    () => initialSessionFraming?.panToken ?? 0,
  );
  const [fitEpoch, setFitEpoch] = useState(() =>
    initialSessionFraming?.bumpFitEpoch ? 1 : 0,
  );
  const [radiusFitEpoch, setRadiusFitEpoch] = useState(() =>
    initialSessionFraming?.bumpRadiusFitEpoch ? 1 : 0,
  );
  const [boundsFitEpoch, setBoundsFitEpoch] = useState(() =>
    initialSessionFraming?.bumpBoundsFitEpoch ? 1 : 0,
  );
  const [framingMode, setFramingMode] = useState<ShopMapFramingMode>(
    () => initialSessionFraming?.framingMode ?? 'shops',
  );
  const [checkedBuiltin, setCheckedBuiltin] = useState<ShopMapBuiltinId | null>(
    () => initialSessionFraming?.checkedBuiltin ?? 'general',
  );
  const [activeViewId, setActiveViewId] = useState<number | null>(
    () => initialSessionFraming?.activeViewId ?? null,
  );
  const [ephemeralRadiusKm, setEphemeralRadiusKm] = useState<number | null>(
    () => initialSessionFraming?.ephemeralRadiusKm ?? null,
  );
  const [radiusCenter, setRadiusCenter] =
    useState<ShopsDirectoryMapUserPosition | null>(
      () => initialSessionFraming?.radiusCenter ?? null,
    );
  /** Ephemeral Around-me tracks live GPS; remount/saved FREEZE uses radiusCenter only. */
  const [radiusLiveGps, setRadiusLiveGps] = useState(
    () => initialSessionFraming?.radiusLiveGps ?? false,
  );
  const [boundsOverlay, setBoundsOverlay] =
    useState<ShopMapBoundsOverlay | null>(null);
  const [viewsOpen, setViewsOpen] = useState(false);
  /** G6 — park null stays on map; aria-live until successful re-park. */
  const [signInParkFailed, setSignInParkFailed] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardPrefill, setWizardPrefill] =
    useState<ShopMapViewWizardPrefill | null>(null);
  /** Wizard configure preview — overlays only; never filters pins (frame≠filter). */
  const [wizardFramingPreview, setWizardFramingPreview] =
    useState<ShopMapWizardFramingPreview>({ mode: 'none' });
  /** ADR-MV-09 — below-sm map_camera dismiss-until-Save. */
  const [mapCameraCaptureActive, setMapCameraCaptureActive] = useState(false);
  const [mapCameraSaveToken, setMapCameraSaveToken] = useState(0);
  /** G13 — configureError while capture chrome dismissed. */
  const [mapCameraCaptureError, setMapCameraCaptureError] = useState<
    string | null
  >(null);
  const cameraBoundsGetterRef = useRef<
    (() => ShopMapBoundsOverlay | null) | null
  >(null);
  const viewsTriggerRef = useRef<HTMLButtonElement>(null);
  const awaitingPanAfterAcquireRef = useRef(false);
  const awaitingAroundMeAfterAcquireRef = useRef(false);
  const pendingAroundMeKmRef = useRef<number | null>(null);
  const restoredSavedViewIdRef = useRef<number | null>(
    initialSessionFraming?.activeViewId ?? null,
  );

  const viewsMenuId = 'customer-shops-directory-views-menu';

  const mapItems = useMemo(() => buildMapItems(visibleItems), [visibleItems]);
  const boundsKey = useMemo(() => computeFitBoundsKey(mapItems), [mapItems]);
  const corners = useMemo(() => latLngBoundsFromItems(mapItems), [mapItems]);
  const userPresenceKey: '0' | '1' = userPosition !== null ? '1' : '0';

  const selectedItem = useMemo(
    () =>
      selectedSalesPointId === null
        ? null
        : (visibleItems.find((row) => row.salesPointId === selectedSalesPointId) ??
          null),
    [selectedSalesPointId, visibleItems],
  );

  const selectedDistanceMeters = useMemo(() => {
    if (selectedItem === null || userPosition === null) {
      return null;
    }
    if (
      typeof selectedItem.lat !== 'number' ||
      typeof selectedItem.lng !== 'number'
    ) {
      return null;
    }
    return haversineMeters(
      userPosition.lat,
      userPosition.lng,
      selectedItem.lat,
      selectedItem.lng,
    );
  }, [selectedItem, userPosition]);

  let initialCenter: LatLngExpression = [
    CZECHIA_FALLBACK_CENTER[0],
    CZECHIA_FALLBACK_CENTER[1],
  ];
  let initialZoom = DEFAULT_MAP_ZOOM;
  if (restoredViewport !== null) {
    initialCenter = [restoredViewport.centerLat, restoredViewport.centerLng];
    initialZoom = restoredViewport.zoom;
  } else if (mapItems.length > 0) {
    const first = mapItems[0];
    if (first) {
      initialCenter = [first.lat, first.lng];
    }
  } else if (userPosition) {
    initialCenter = [userPosition.lat, userPosition.lng];
  }

  const clearOverlays = useCallback((): void => {
    setEphemeralRadiusKm(null);
    setRadiusCenter(null);
    setBoundsOverlay(null);
  }, []);

  /** General → shops; clear overlays; panToken=0; epochs 0; bump fitEpoch (UNIT-G3-GEN-EPOCH). */
  const handleSelectGeneral = useCallback((): void => {
    if (lockHostCamera) {
      return;
    }
    awaitingPanAfterAcquireRef.current = false;
    awaitingAroundMeAfterAcquireRef.current = false;
    pendingAroundMeKmRef.current = null;
    clearOverlays();
    setRadiusLiveGps(false);
    setPanToken(0);
    setFramingMode('shops');
    setCheckedBuiltin('general');
    setActiveViewId(null);
    setRadiusFitEpoch(0);
    setBoundsFitEpoch(0);
    setFitEpoch((n) => n + 1);
    persistShopsMapViewActive(buildGeneralActive());
  }, [clearOverlays, lockHostCamera, persistShopsMapViewActive]);

  /** My location → pan-only; clears Circle/Rectangle. */
  const handleSelectMyLocation = useCallback((): void => {
    if (lockHostCamera) {
      return;
    }
    awaitingAroundMeAfterAcquireRef.current = false;
    pendingAroundMeKmRef.current = null;
    clearOverlays();
    setRadiusLiveGps(false);
    setFramingMode('myLocation');
    setCheckedBuiltin('myLocation');
    setActiveViewId(null);
    setRadiusFitEpoch(0);
    setBoundsFitEpoch(0);
    persistShopsMapViewActive(buildMyLocationActive());
    if (userPosition !== null) {
      setPanToken((n) => n + 1);
      return;
    }
    awaitingPanAfterAcquireRef.current = true;
    onRequestMyLocation();
  }, [
    clearOverlays,
    lockHostCamera,
    onRequestMyLocation,
    persistShopsMapViewActive,
    userPosition,
  ]);

  const applyAroundMeRadius = useCallback(
    (radiusKm: number, center: ShopsDirectoryMapUserPosition): void => {
      if (lockHostCamera) {
        return;
      }
      setBoundsOverlay(null);
      setFramingMode('radius');
      setCheckedBuiltin('aroundMe');
      setActiveViewId(null);
      setEphemeralRadiusKm(radiusKm);
      setRadiusCenter(center);
      setRadiusLiveGps(true);
      setPanToken(0);
      setBoundsFitEpoch(0);
      setRadiusFitEpoch((n) => n + 1);
      persistShopsMapViewActive(
        buildEphemeralAroundMeActive(radiusKm, center),
      );
    },
    [lockHostCamera, persistShopsMapViewActive],
  );

  const handleApplyAroundMe = useCallback(
    (radiusKm: number): void => {
      if (lockHostCamera) {
        return;
      }
      if (userPosition !== null) {
        applyAroundMeRadius(radiusKm, userPosition);
        return;
      }
      pendingAroundMeKmRef.current = radiusKm;
      awaitingAroundMeAfterAcquireRef.current = true;
      onRequestMyLocation({
        purpose: 'aroundMeApply',
        onDenied: () => {
          awaitingAroundMeAfterAcquireRef.current = false;
          pendingAroundMeKmRef.current = null;
        },
      });
    },
    [applyAroundMeRadius, lockHostCamera, onRequestMyLocation, userPosition],
  );

  /**
   * Apply saved custom view geometry (ADR-MV-04 FREEZE for radius kinds).
   * Parent supplies `savedViews`; session only stores viewId + mode.
   */
  const applySavedViewFraming = useCallback(
    (viewId: number, dtoOverride?: MapCustomViewDTO): boolean => {
      if (lockHostCamera) {
        return false;
      }
      const row =
        dtoOverride !== undefined
          ? shopMapSavedViewRowFromDto(dtoOverride)
          : (savedViews.find((v) => v.id === viewId) ?? null);
      if (row === null) {
        return false;
      }
      const applied = framingApplyFromSavedViewRow(row);
      if (applied === null) {
        return false;
      }

      awaitingPanAfterAcquireRef.current = false;
      awaitingAroundMeAfterAcquireRef.current = false;
      pendingAroundMeKmRef.current = null;
      setWizardFramingPreview({ mode: 'none' });
      setActiveViewId(viewId);
      setCheckedBuiltin(null);
      setPanToken(0);

      persistShopsMapViewActive(
        buildSavedViewActive(String(viewId), applied.framingMode),
      );

      if (applied.framingMode === 'radius') {
        setBoundsOverlay(null);
        setFramingMode('radius');
        setEphemeralRadiusKm(applied.radiusKm);
        setRadiusCenter({
          lat: applied.centerLat,
          lng: applied.centerLng,
        });
        setRadiusLiveGps(false);
        setBoundsFitEpoch(0);
        setRadiusFitEpoch((n) => n + 1);
        return true;
      }

      setEphemeralRadiusKm(null);
      setRadiusCenter(null);
      setRadiusLiveGps(false);
      setBoundsOverlay(applied.boundsOverlay);
      setFramingMode('bounds');
      setRadiusFitEpoch(0);
      setBoundsFitEpoch((n) => n + 1);
      return true;
    },
    [lockHostCamera, persistShopsMapViewActive, savedViews],
  );

  useEffect(() => {
    if (userPosition === null || !awaitingPanAfterAcquireRef.current) {
      return;
    }
    awaitingPanAfterAcquireRef.current = false;
    const frame = window.requestAnimationFrame(() => {
      setFramingMode('myLocation');
      setPanToken((n) => n + 1);
    });
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [userPosition]);

  useEffect(() => {
    if (userPosition === null || !awaitingAroundMeAfterAcquireRef.current) {
      return;
    }
    const km = pendingAroundMeKmRef.current;
    awaitingAroundMeAfterAcquireRef.current = false;
    pendingAroundMeKmRef.current = null;
    if (km === null) {
      return;
    }
    applyAroundMeRadius(km, userPosition);
  }, [applyAroundMeRadius, userPosition]);

  useEffect(() => {
    if (viewsOpen) {
      onSelectSalesPointId(null);
    }
  }, [viewsOpen, onSelectSalesPointId]);

  /** My location remount — pan once when parent already has GPS (no getCurrentPosition here). */
  useEffect(() => {
    if (
      framingMode !== 'myLocation' ||
      userPosition === null ||
      panToken > 0
    ) {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      setPanToken((n) => n + 1);
    });
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [framingMode, panToken, userPosition]);

  /**
   * G6 — park-fail UX parity with CheckoutGuestSignInPrompt: navigate only when
   * parked !== null; null sets recoverable aria-live and returns false so the
   * Views menu stays open (fail-closed).
   */
  const handleSignInToSaveViews = useCallback((): boolean => {
    // ADR-MV-10 draft SSOT + ADR-MV-11 elevate-park (payment-resume peer).
    // G2: thin guest draft is fallback only — beginSignIn merges without overwriting
    // a richer customer:directory:mapViewWizardDraft:v1 already in session.
    const path = `${location.pathname}${location.search}`;
    const result = beginSignInToSaveMapViews({
      path,
      tenantCode,
      draft: buildGuestSignInMapViewDraft(),
    });
    if (result.parked === null) {
      setSignInParkFailed(true);
      return false;
    }
    setSignInParkFailed(false);
    void navigate(result.signInHref);
    return true;
  }, [location.pathname, location.search, navigate, tenantCode]);

  const handleAddCustomView = useCallback((): void => {
    setViewsOpen(false);
    setWizardPrefill(null);
    setWizardFramingPreview({ mode: 'none' });
    setMapCameraCaptureActive(false);
    setWizardOpen(true);
  }, []);

  const handleSaveThisView = useCallback((): void => {
    setViewsOpen(false);
    setWizardPrefill({
      kind: 'around_me',
      radiusKm: ephemeralRadiusKm ?? undefined,
    });
    setWizardFramingPreview({ mode: 'none' });
    setMapCameraCaptureActive(false);
    setWizardOpen(true);
  }, [ephemeralRadiusKm]);

  /** ADR-MV-10 — resume wizard draft once after sign-in / remount (adjust during render). */
  const [draftResumeDone, setDraftResumeDone] = useState(false);
  if (signedIn && !draftResumeDone) {
    setDraftResumeDone(true);
    const draft = readMapViewWizardDraftFromSession();
    if (draft !== null && !wizardOpen) {
      // G2: open without thin prefill so ShopMapViewWizard hydrates full session draft.
      setWizardPrefill(null);
      setWizardOpen(true);
    }
  }

  const handleWizardFramingPreviewChange = useCallback(
    (preview: ShopMapWizardFramingPreview): void => {
      setWizardFramingPreview(preview);
      if (preview.mode === 'radius') {
        setRadiusFitEpoch((n) => n + 1);
      } else if (preview.mode === 'bounds') {
        setBoundsFitEpoch((n) => n + 1);
      }
    },
    [],
  );

  const getMapBounds = useCallback((): ShopMapBoundsOverlay | null => {
    return cameraBoundsGetterRef.current?.() ?? null;
  }, []);

  /**
   * P14a saved viewId — re-apply FREEZE geometry when list arrives (session holds id+mode only).
   * G2/G3: unsettled loading (incl. offline/paused pending) must not false-General on [];
   * settled empty/missing/failed apply → General.
   */
  useEffect(() => {
    const viewId = restoredSavedViewIdRef.current;
    if (viewId === null) {
      return;
    }
    if (savedViewsLoading) {
      return;
    }
    // G10: settled error is not empty — keep pending restore until retry succeeds.
    if (savedViewsError) {
      return;
    }
    // Settled: empty list, missing id, or apply failure → General/shops (clear overlays).
    if (
      savedViews.length === 0 ||
      !savedViews.some((row) => row.id === viewId)
    ) {
      restoredSavedViewIdRef.current = null;
      handleSelectGeneral();
      return;
    }
    restoredSavedViewIdRef.current = null;
    if (applySavedViewFraming(viewId)) {
      onApplySavedView?.(viewId);
    } else {
      handleSelectGeneral();
    }
  }, [
    applySavedViewFraming,
    handleSelectGeneral,
    onApplySavedView,
    savedViews,
    savedViewsError,
    savedViewsLoading,
  ]);

  const showNoCoordinates = mapReady && mapItems.length === 0;
  // A19 — when geo/map omits ONLINE_ONLY shops, show Shipping-only empty copy (not generic noCoordinates).
  const showShippingOnlyGeoOmit =
    showNoCoordinates &&
    shouldShowShippingOnlyGeoOmitEmpty({
      plottableCount: mapItems.length,
      items: visibleItems,
    });
  const showMapContainer = !showNoCoordinates && (mapReady || mapItems.length > 0);

  const liveRadiusCenter =
    framingMode === 'radius' &&
    checkedBuiltin === 'aroundMe' &&
    radiusLiveGps &&
    userPosition !== null
      ? userPosition
      : radiusCenter;

  const wizardRadiusPreview =
    wizardFramingPreview.mode === 'radius' ? wizardFramingPreview : null;
  const wizardBoundsPreview =
    wizardFramingPreview.mode === 'bounds' ? wizardFramingPreview : null;

  const circleCenter =
    wizardRadiusPreview !== null
      ? {
          lat: wizardRadiusPreview.centerLat,
          lng: wizardRadiusPreview.centerLng,
        }
      : liveRadiusCenter;
  const radiusMeters = ((): number | null => {
    if (wizardRadiusPreview !== null) {
      return radiusKmToMeters(wizardRadiusPreview.radiusKm);
    }
    if (ephemeralRadiusKm !== null) {
      return radiusKmToMeters(ephemeralRadiusKm);
    }
    return null;
  })();

  const showRadiusCircle =
    (wizardRadiusPreview !== null || framingMode === 'radius') &&
    circleCenter !== null &&
    radiusMeters !== null;

  const activeBoundsOverlay: ShopMapBoundsOverlay | null = ((): ShopMapBoundsOverlay | null => {
    if (wizardBoundsPreview !== null) {
      return {
        swLat: wizardBoundsPreview.swLat,
        swLng: wizardBoundsPreview.swLng,
        neLat: wizardBoundsPreview.neLat,
        neLng: wizardBoundsPreview.neLng,
      };
    }
    if (framingMode === 'bounds') {
      return boundsOverlay;
    }
    return null;
  })();

  const rectangleBounds =
    activeBoundsOverlay !== null
      ? ([
          [activeBoundsOverlay.swLat, activeBoundsOverlay.swLng],
          [activeBoundsOverlay.neLat, activeBoundsOverlay.neLng],
        ] as [[number, number], [number, number]])
      : null;

  const radiusFitFramingMode: ShopMapFramingMode =
    wizardRadiusPreview !== null ? 'radius' : framingMode;
  const boundsFitFramingMode: ShopMapFramingMode =
    wizardBoundsPreview !== null ? 'bounds' : framingMode;

  const portalTarget = typeof document !== 'undefined' ? document.body : null;

  const overlays =
    !hideViewsChrome && portalTarget !== null
      ? createPortal(
          <>
            <ShopMapViewsMenu
              open={viewsOpen}
              onClose={() => {
                setViewsOpen(false);
              }}
              anchorRef={viewsTriggerRef}
              checkedBuiltin={checkedBuiltin}
              activeViewId={activeViewId}
              ephemeralAroundMeActive={
                framingMode === 'radius' &&
                checkedBuiltin === 'aroundMe' &&
                ephemeralRadiusKm !== null
              }
              savedViews={savedViews}
              signedIn={signedIn}
              onSelectGeneral={handleSelectGeneral}
              onSelectMyLocation={handleSelectMyLocation}
              onApplyAroundMe={handleApplyAroundMe}
              onApplySavedView={(viewId) => {
                if (applySavedViewFraming(viewId)) {
                  onApplySavedView?.(viewId);
                }
              }}
              onAddCustomView={handleAddCustomView}
              onSaveThisView={handleSaveThisView}
              onSignInToSaveViews={handleSignInToSaveViews}
              signInParkFailed={signInParkFailed}
              onRequestDeleteView={(viewId) => {
                if (activeViewId === viewId) {
                  handleSelectGeneral();
                }
                onRequestDeleteView?.(viewId);
              }}
              onRequestRenameView={onRequestRenameView}
              deletePending={deletePending}
              renamePending={renamePending}
            />
            <ShopMapViewWizard
              open={wizardOpen}
              onClose={() => {
                setWizardOpen(false);
                setWizardPrefill(null);
                setWizardFramingPreview({ mode: 'none' });
                setMapCameraCaptureActive(false);
                setMapCameraCaptureError(null);
              }}
              prefill={wizardPrefill}
              userPosition={userPosition}
              onRequestMyLocation={onRequestMyLocation}
              getMapBounds={getMapBounds}
              onFramingPreviewChange={handleWizardFramingPreviewChange}
              onMapCameraCaptureActiveChange={setMapCameraCaptureActive}
              onMapCameraCaptureErrorChange={setMapCameraCaptureError}
              mapCameraSaveToken={mapCameraSaveToken}
              onCreated={(view) => {
                applySavedViewFraming(view.id, view);
                setWizardOpen(false);
                setWizardPrefill(null);
                setWizardFramingPreview({ mode: 'none' });
                setMapCameraCaptureActive(false);
                setMapCameraCaptureError(null);
              }}
            />
          </>,
          portalTarget,
        )
      : null;

  return (
    <>
      <div
        className={[
          'relative z-0 isolate flex min-h-0 max-h-full w-full min-w-0 flex-1 flex-col overflow-hidden overscroll-contain',
          'rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-elevated)] [touch-action:manipulation]',
          // Portrait / tall: dvh mins. Short landscape: min-h-0 so flex leftover wins
          // (40dvh of 390px forced overflow under header+nav and zeroed Leaflet).
          'min-h-[min(40dvh,16rem,100%)] sm:min-h-[min(50dvh,24rem,100%)] lg:min-h-[min(55dvh,32rem,100%)]',
          '[@media(orientation:landscape)_and_(max-height:500px)]:min-h-0',
        ].join(' ')}
        data-testid="customer-shops-directory-map"
        data-framing-mode={framingMode}
        data-fit-epoch={fitEpoch}
        data-radius-fit-epoch={radiusFitEpoch}
        data-bounds-fit-epoch={boundsFitEpoch}
        data-radius-meters={
          showRadiusCircle && radiusMeters !== null
            ? String(radiusMeters)
            : undefined
        }
      >
        <div className="flex shrink-0 flex-col gap-1 border-b border-[var(--color-border)] px-2 py-1.5">
          <div className="flex items-center justify-between gap-2">
            <p className="px-1 text-xs font-semibold uppercase tracking-wide text-[var(--color-on-surface-muted)]">
              {t('directory.mapView.map')}
            </p>
            {!hideViewsChrome ? (
              <button
                ref={viewsTriggerRef}
                type="button"
                className="inline-flex h-11 min-h-11 min-w-11 items-center justify-center rounded-full px-3 text-sm font-medium text-[var(--color-on-surface)] hover:bg-[var(--color-surface-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]"
                data-testid="customer-shops-directory-views-trigger"
                aria-haspopup="dialog"
                aria-expanded={viewsOpen}
                aria-controls={viewsMenuId}
                aria-label={t('directory.mapView.viewsTriggerAria')}
                onClick={() => {
                  setViewsOpen((open) => !open);
                }}
              >
                {t('directory.mapView.viewsTrigger')}
              </button>
            ) : null}
          </div>
          {/* G6 — chrome banner while menu closed (menu shows the same fail inline). */}
          {signInParkFailed && !viewsOpen && !hideViewsChrome ? (
            <p
              role="status"
              aria-live="polite"
              className="px-1 text-sm text-[var(--color-on-surface-muted)]"
              data-testid="customer-shops-directory-sign-in-park-failed"
            >
              {t('directory.mapView.signInParkFailed', {
                defaultValue:
                  'Could not save your return to the map. Stay here and try Sign in again.',
              })}
            </p>
          ) : null}
        </div>

        <div className="relative z-0 min-h-0 min-w-0 flex-1 overflow-hidden">
          {showNoCoordinates ? (
            <div
              className="flex h-full min-h-0 flex-col items-center justify-center gap-3 px-4 text-center"
              data-testid={
                showShippingOnlyGeoOmit
                  ? 'customer-shops-directory-map-shipping-only-omit'
                  : 'customer-shops-directory-map-no-coordinates'
              }
            >
              <p className="text-sm text-[var(--color-on-surface-muted)]">
                {showShippingOnlyGeoOmit
                  ? t(CUSTOMER_PRESENCE_MAP_OMITS_SHIPPING_ONLY_I18N_KEY)
                  : t('directory.mapView.noCoordinates')}
              </p>
              {onBackToList ? (
                <Button
                  type="button"
                  intent="secondary"
                  data-testid="customer-shops-directory-map-back-list"
                  onClick={onBackToList}
                >
                  {t('directory.mapView.backToList')}
                </Button>
              ) : null}
            </div>
          ) : null}
          {showMapContainer ? (
            <MapContainer
              key={`map-${tileEpoch}`}
              center={initialCenter}
              zoom={initialZoom}
              className="h-full w-full"
              scrollWheelZoom={false}
              attributionControl
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                eventHandlers={{
                  tileerror: () => {
                    setTileError(true);
                  },
                }}
              />
              <FitBoundsWhenReady
                boundsKey={boundsKey}
                corners={corners}
                userPosition={userPosition}
                mapReady={mapReady}
                userPresenceKey={userPresenceKey}
                fitEpoch={fitEpoch}
                framingMode={framingMode}
                suppressFitForRestoredViewport={suppressFitForRestoredViewport}
              />
              <PanToUserPosition
                userPosition={userPosition}
                panToken={panToken}
                framingMode={framingMode}
                lockHostCamera={lockHostCamera}
              />
              <FitRadiusOverlay
                center={circleCenter}
                radiusKm={
                  wizardRadiusPreview !== null
                    ? wizardRadiusPreview.radiusKm
                    : ephemeralRadiusKm
                }
                radiusFitEpoch={radiusFitEpoch}
                framingMode={radiusFitFramingMode}
                lockHostCamera={lockHostCamera}
              />
              <FitBoundsOverlay
                overlay={activeBoundsOverlay}
                boundsFitEpoch={boundsFitEpoch}
                framingMode={boundsFitFramingMode}
                lockHostCamera={lockHostCamera}
              />
              <MapViewportSessionBridge
                restoredViewport={restoredViewport}
                onViewportChange={onViewportChange}
              />
              <MapCameraBoundsBridge boundsGetterRef={cameraBoundsGetterRef} />
              <MapClickClear
                onClear={() => {
                  onSelectSalesPointId(null);
                }}
              />
              {showRadiusCircle &&
              circleCenter !== null &&
              radiusMeters !== null ? (
                <Circle
                  center={[circleCenter.lat, circleCenter.lng]}
                  radius={radiusMeters}
                  pathOptions={{
                    color: 'var(--color-accent)',
                    fillColor: 'var(--color-accent)',
                    fillOpacity: 0.12,
                    weight: 2,
                  }}
                  data-testid="customer-shops-directory-map-radius-circle"
                />
              ) : null}
              {rectangleBounds !== null ? (
                <Rectangle
                  bounds={rectangleBounds}
                  pathOptions={{
                    color: 'var(--color-accent)',
                    fillColor: 'var(--color-accent)',
                    fillOpacity: 0.08,
                    weight: 2,
                  }}
                  data-testid="customer-shops-directory-map-bounds-rectangle"
                />
              ) : null}
              {mapItems.map((item) => (
                <Marker
                  key={salesPointMapMarkerKey(item.salesPointId, item.lat, item.lng)}
                  position={[item.lat, item.lng]}
                  icon={shopMarkerIcon}
                  eventHandlers={{
                    click: (event) => {
                      event.originalEvent.stopPropagation();
                      onSelectSalesPointId(item.salesPointId);
                    },
                  }}
                />
              ))}
              {userPosition ? (
                <Marker
                  position={[userPosition.lat, userPosition.lng]}
                  icon={userPositionIcon}
                  zIndexOffset={1000}
                />
              ) : null}
            </MapContainer>
          ) : null}

          {!mapReady ? (
            <div
              className="absolute inset-0 z-[400] flex items-center justify-center bg-[var(--color-surface)]/70 backdrop-blur-[1px]"
              data-testid="customer-shops-directory-map-loading"
              aria-live="polite"
            >
              <p className="text-sm font-medium text-[var(--color-on-surface)]">
                {t('directory.mapView.loadingShops')}
              </p>
            </div>
          ) : null}

          {mapCameraCaptureActive ? (
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 z-[450] flex justify-center px-3 pt-3 pb-[max(3.25rem,calc(env(safe-area-inset-bottom)+2.5rem))]"
              data-testid="customer-shops-directory-wizard-map-camera-capture"
              data-map-camera-attribution-clearance="true"
            >
              <div className="pointer-events-auto w-full max-w-sm rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-elevated)] p-3 shadow-[var(--shadow-popover)]">
                {mapCameraCaptureError !== null ? (
                  <p
                    className="mb-2 text-sm text-[var(--color-danger)]"
                    role="alert"
                    data-testid="customer-shops-directory-wizard-configure-error"
                  >
                    {mapCameraCaptureError}
                  </p>
                ) : null}
                <p className="mb-2 text-center text-xs text-[var(--color-on-surface-muted)]">
                  {t('directory.mapView.wizardMapCameraSplitHint')}
                </p>
                <Button
                  intent="primary"
                  type="button"
                  block
                  data-testid="customer-shops-directory-wizard-map-camera-save"
                  onClick={() => {
                    setMapCameraSaveToken((n) => n + 1);
                  }}
                >
                  {t('directory.mapView.saveThisView')}
                </Button>
              </div>
            </div>
          ) : null}

          {tileError ? (
            <div
              className="absolute inset-x-3 top-3 z-[450] rounded-lg border border-[var(--color-danger)] bg-[var(--color-surface)] p-3 shadow"
              role="alert"
              data-testid="customer-shops-directory-map-tile-error"
            >
              <p className="text-sm font-semibold text-[var(--color-danger)]">
                {t('directory.mapView.unavailable')}
              </p>
              <p className="mt-1 text-sm text-[var(--color-on-surface-muted)]">
                {t('directory.mapView.tileError')}
              </p>
              <Button
                type="button"
                intent="secondary"
                className="mt-2"
                data-testid="customer-shops-directory-map-tile-retry"
                onClick={() => {
                  setTileError(false);
                  setTileEpoch((n) => n + 1);
                }}
              >
                {t('directory.mapView.retry')}
              </Button>
            </div>
          ) : null}

          {savedViewsError ? (
            <div
              className="absolute inset-x-3 top-3 z-[450] rounded-lg border border-[var(--color-danger)] bg-[var(--color-surface)] p-3 shadow"
              role="alert"
              data-testid="customer-shops-directory-map-views-error"
            >
              <p className="text-sm font-semibold text-[var(--color-danger)]">
                {t('directory.mapView.savedViewsLoadFailed')}
              </p>
              <Button
                type="button"
                intent="secondary"
                className="mt-2"
                data-testid="customer-shops-directory-map-views-retry"
                onClick={() => {
                  onRetrySavedViews?.();
                }}
              >
                {t('directory.mapView.retry')}
              </Button>
            </div>
          ) : null}

          {selectedItem && showSelectionCard ? (
            <ShopMapMarkerCard
              item={selectedItem}
              persistTenant={persistTenant}
              hideTenantName={hideTenantName}
              showTenantChip={showTenantChipOnSelectionCard}
              distanceMeters={selectedDistanceMeters}
              onClose={() => {
                onSelectSalesPointId(null);
              }}
            />
          ) : null}
        </div>
      </div>
      {overlays}
    </>
  );
}